import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import PageHeader from '../components/PageHeader';
import orgPolicyService, {
  type OrgPolicies,
  type OrgPoliciesUpdate,
  type OvertimeRules,
  type OrgOvertimePolicy,
} from '../services/orgPolicyService';
import { Scale, Clock, Sun, Coffee, Calendar, Stethoscope, Palmtree, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import EmploymentPolicyDocuments from '../components/policies/EmploymentPolicyDocuments';

// ── Shared presentational sub-components ─────────────────────────────────────

function NumberInput({ label, value, onChange, min, max, step = 0.25, suffix, helpText }: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  helpText?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          min={min}
          max={max}
          step={step}
          className="block w-32 rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        />
        {suffix && <span className="text-sm text-neutral-500">{suffix}</span>}
      </div>
      {helpText && <p className="text-xs text-neutral-400 mt-1">{helpText}</p>}
    </div>
  );
}

function Toggle({ label, checked, onChange, helpText }: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  helpText?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
          checked ? 'bg-primary-600' : 'bg-neutral-200'
        }`}
      >
        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`} />
      </button>
      <div>
        <span className="text-sm font-medium text-neutral-700">{label}</span>
        {helpText && <p className="text-xs text-neutral-400 mt-0.5">{helpText}</p>}
      </div>
    </div>
  );
}

function SelectInput({ label, value, onChange, options, helpText }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  helpText?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 mb-1">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="block w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {helpText && <p className="text-xs text-neutral-400 mt-1">{helpText}</p>}
    </div>
  );
}

function SectionHeader({ isExpanded, onToggle, icon, title, description }: {
  isExpanded: boolean;
  onToggle: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-4 text-left hover:bg-neutral-50 transition-colors rounded-t-xl"
    >
      <div className="flex items-center gap-3">
        {icon}
        <div>
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
          <p className="text-sm text-neutral-500">{description}</p>
        </div>
      </div>
      {isExpanded
        ? <ChevronDown className="h-5 w-5 text-neutral-400" />
        : <ChevronRight className="h-5 w-5 text-neutral-400" />
      }
    </button>
  );
}

// Shared overtime fields form used by both the default tab and the freq panel
function OvertimeRulesForm({ rules, onChange }: {
  rules: OvertimeRules;
  onChange: <K extends keyof OvertimeRules>(field: K, value: OvertimeRules[K]) => void;
}) {
  return (
    <>
      {/* Weekday */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Weekday Time Boundaries</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <NumberInput
            label="OT morning ends at"
            value={rules.overtime_end_hour}
            onChange={v => onChange('overtime_end_hour', v)}
            min={0} max={24} step={0.5}
            suffix="(e.g. 6 = 6:00 AM)"
            helpText="Hours before this time are overtime"
          />
          <NumberInput
            label="OT evening starts at"
            value={rules.overtime_start_hour}
            onChange={v => onChange('overtime_start_hour', v)}
            min={0} max={24} step={0.5}
            suffix="(e.g. 18 = 6:00 PM)"
            helpText="Hours after this time are overtime"
          />
        </div>
        <OvertimeRateRow
          multiplier={rules.overtime_multiplier}
          onMultiplierChange={v => onChange('overtime_multiplier', v)}
          label="Weekday OT rate"
        />
      </div>

      {/* Saturday */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Saturday</h4>
        <Toggle
          label="All Saturday hours are overtime"
          checked={rules.saturday_all_overtime}
          onChange={v => onChange('saturday_all_overtime', v)}
          helpText="If off, Saturday uses weekday time-of-day rules"
        />
        <OvertimeRateRow
          multiplier={rules.saturday_multiplier}
          onMultiplierChange={v => onChange('saturday_multiplier', v)}
          label="Saturday OT rate"
        />
      </div>

      {/* Sunday */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Sunday</h4>
        <Toggle
          label="All Sunday hours are overtime"
          checked={rules.sunday_all_overtime}
          onChange={v => onChange('sunday_all_overtime', v)}
          helpText="If off, Sunday uses weekday time-of-day rules"
        />
        <OvertimeRateRow
          multiplier={rules.sunday_multiplier}
          onMultiplierChange={v => onChange('sunday_multiplier', v)}
          label="Sunday OT rate"
        />
      </div>
    </>
  );
}

// Shared rate selector: paid OT multiplier OR Time in Lieu (1:1)
// Overtime is paid at this rate. Whether someone banks it as time in lieu
// instead is their own agreement with the organisation (2026-10-08), not a
// setting here: these switches used to bank time in lieu for whole pay groups
// with nobody's agreement on record.
function OvertimeRateRow({ multiplier, onMultiplierChange, label }: {
  multiplier: number;
  onMultiplierChange: (v: number) => void;
  label: string;
}) {
  return (
    <NumberInput
      label={label}
      value={multiplier}
      onChange={onMultiplierChange}
      min={1} max={5} step={0.25}
      suffix="x"
      helpText="Paid overtime rate. Someone with a time in lieu agreement banks these hours 1:1 instead."
    />
  );
}

// Handles the weekly or fortnightly tab — loads its own draft state
function FrequencyOvertimePanel({ orgId, frequency, defaultRules, policy, onPolicyChange }: {
  orgId: number;
  frequency: 'weekly' | 'fortnightly';
  defaultRules: OvertimeRules;
  policy: OrgOvertimePolicy | null;
  onPolicyChange: (p: OrgOvertimePolicy | null) => void;
}) {
  const [isCustomising, setIsCustomising] = useState(false);
  const isCustomised = policy !== null || isCustomising;
  const [draft, setDraft] = useState<OvertimeRules>(policy ?? { ...defaultRules });
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState(false);

  // Sync draft when policy prop changes (e.g. after parent reloads)
  useEffect(() => {
    setDraft(policy ?? { ...defaultRules });
  }, [policy]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCustomise = () => {
    setDraft({ ...defaultRules });
    setIsCustomising(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setLocalError(null);
      const res = await orgPolicyService.upsertOrgOvertimePolicy(orgId, frequency, draft);
      if (res.success && res.data) {
        onPolicyChange(res.data);
        setLocalSuccess(true);
        setTimeout(() => setLocalSuccess(false), 3000);
      } else {
        setLocalError('Failed to save');
      }
    } catch {
      setLocalError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    try {
      setSaving(true);
      setLocalError(null);
      await orgPolicyService.deleteOrgOvertimePolicy(orgId, frequency);
      onPolicyChange(null);
      setIsCustomising(false);
    } catch {
      setLocalError('Failed to clear override');
    } finally {
      setSaving(false);
    }
  };

  const label = frequency === 'weekly' ? 'weekly' : 'fortnightly';

  return (
    <div className="space-y-6">
      {!isCustomised ? (
        <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex-1">
            <p className="text-sm font-medium text-neutral-700">Using org default rules</p>
            <p className="text-xs text-neutral-500 mt-0.5">
              {frequency === 'weekly' ? 'Weekly' : 'Fortnightly'} staff use the same overtime rules as the org default.
              Customise to set different rules for this pay group.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={handleCustomise}>
            Customise
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-1 rounded">
              Custom rules active — overrides org default for {label} staff
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={handleClear}
              disabled={saving}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Revert to default
            </Button>
          </div>
          {localError && <p className="text-sm text-red-600">{localError}</p>}
          {localSuccess && <p className="text-sm text-green-600">Rules saved successfully</p>}
          <OvertimeRulesForm
            rules={draft}
            onChange={(field, value) => setDraft(prev => ({ ...prev, [field]: value }))}
          />
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="sm">
              {saving ? 'Saving…' : `Save ${label} rules`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CompanyPolicies() {
  const { user } = useAuth();
  const orgId = user?.organisation_id;

  const [policies, setPolicies] = useState<OrgPolicies | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Per-frequency overtime policies (null = not customised, falls back to org default)
  const [overtimeTab, setOvertimeTab] = useState<'default' | 'weekly' | 'fortnightly'>('default');
  const [weeklyOTPolicy, setWeeklyOTPolicy] = useState<OrgOvertimePolicy | null>(null);
  const [fortnightlyOTPolicy, setFortnightlyOTPolicy] = useState<OrgOvertimePolicy | null>(null);

  // Track which sections are expanded
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    overtime: true,
    thresholds: true,
    lunch: false,
    publicHoliday: false,
    sickLeave: false,
    leaveAccrual: false,
    documents: true,
  });

  const toggleSection = (key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const loadPolicies = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const [baseRes, weeklyRes, fortnightlyRes] = await Promise.all([
        orgPolicyService.getPolicies(orgId),
        orgPolicyService.getOrgOvertimePolicy(orgId, 'weekly'),
        orgPolicyService.getOrgOvertimePolicy(orgId, 'fortnightly'),
      ]);
      if (baseRes.success && baseRes.data) {
        setPolicies(baseRes.data);
      } else {
        setError('Failed to load company policies');
      }
      if (weeklyRes.success) setWeeklyOTPolicy(weeklyRes.data ?? null);
      if (fortnightlyRes.success) setFortnightlyOTPolicy(fortnightlyRes.data ?? null);
    } catch {
      setError('Failed to load company policies');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const updateField = <K extends keyof OrgPolicies>(field: K, value: OrgPolicies[K]) => {
    if (!policies) return;
    setPolicies({ ...policies, [field]: value });
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!orgId || !policies) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const update: OrgPoliciesUpdate = {
        overtime_start_hour:   policies.overtime_start_hour,
        overtime_end_hour:     policies.overtime_end_hour,
        overtime_multiplier:   policies.overtime_multiplier,
        overtime_til_mode:     policies.overtime_til_mode,
        saturday_all_overtime: policies.saturday_all_overtime,
        saturday_multiplier:   policies.saturday_multiplier,
        saturday_til_mode:     policies.saturday_til_mode,
        sunday_all_overtime:   policies.sunday_all_overtime,
        sunday_multiplier:     policies.sunday_multiplier,
        sunday_til_mode:       policies.sunday_til_mode,
        weekly_overtime_threshold_hours: policies.weekly_overtime_threshold_hours,
        fortnightly_overtime_threshold_hours: policies.fortnightly_overtime_threshold_hours,
        fortnightly_excess_category: policies.fortnightly_excess_category,
        lunch_deduction_threshold_hours: policies.lunch_deduction_threshold_hours,
        ph_require_adjacent_workday: policies.ph_require_adjacent_workday,
        ph_noshow_action: policies.ph_noshow_action,
        sick_cert_required_after_days: policies.sick_cert_required_after_days,
        sick_cert_enforcement: policies.sick_cert_enforcement,
        sick_cert_always_required: policies.sick_cert_always_required,
        sick_cert_adjacent_to_weekend: policies.sick_cert_adjacent_to_weekend,
        sick_cert_adjacent_to_public_holiday: policies.sick_cert_adjacent_to_public_holiday,
        sick_cert_adjacent_to_other_leave: policies.sick_cert_adjacent_to_other_leave,
        sick_cert_consecutive_split_detection: policies.sick_cert_consecutive_split_detection,
        annual_leave_accrual_hours: policies.annual_leave_accrual_hours,
        personal_leave_accrual_hours: policies.personal_leave_accrual_hours,
        long_service_leave_accrual_hours: policies.long_service_leave_accrual_hours,
        annual_leave_max_balance_hours: policies.annual_leave_max_balance_hours,
        personal_leave_max_balance_hours: policies.personal_leave_max_balance_hours,
      };

      const response = await orgPolicyService.updatePolicies(orgId, update);
      if (response.success) {
        setPolicies(response.data);
        const reissued = orgPolicyService.reissuedMessage(response);
        setSuccess(reissued ? `Company policies saved. ${reissued}` : 'Company policies saved successfully');
        setTimeout(() => setSuccess(null), reissued ? 10000 : 4000);
      } else {
        setError('Failed to save policies');
      }
    } catch {
      setError('Failed to save policies');
    } finally {
      setSaving(false);
    }
  };

  // Per HHjsFrontEnd CLAUDE.md, module features gate on the licence ONLY —
  // platform admin/super_admin do NOT bypass module licence checks.
  const { hasAccess: hasHrLicense } = useModuleLicense('timesheet', 'hr');

  if (!hasHrLicense) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Scale className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Timesheet HR licence seat is required to manage company policies.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Company Policies"
          subtitle="Configure overtime, leave, and payroll rules for your organisation"
          icon={<Scale className="h-6 w-6 text-white" />}
        />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-24 bg-white rounded-xl border border-neutral-200" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!policies) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Company Policies"
          subtitle="Configure overtime, leave, and payroll rules for your organisation"
          icon={<Scale className="h-6 w-6 text-white" />}
        />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <p className="text-red-600">{error || 'Failed to load policies'}</p>
        </div>
      </div>
    );
  }

  const defaultOTRules: OvertimeRules = {
    overtime_start_hour:   policies.overtime_start_hour,
    overtime_end_hour:     policies.overtime_end_hour,
    overtime_multiplier:   policies.overtime_multiplier,
    overtime_til_mode:     policies.overtime_til_mode,
    saturday_all_overtime: policies.saturday_all_overtime,
    saturday_multiplier:   policies.saturday_multiplier,
    saturday_til_mode:     policies.saturday_til_mode,
    sunday_all_overtime:   policies.sunday_all_overtime,
    sunday_multiplier:     policies.sunday_multiplier,
    sunday_til_mode:       policies.sunday_til_mode,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Company Policies"
        subtitle="Configure overtime, leave, and payroll rules for your organisation"
        icon={<Scale className="h-6 w-6 text-white" />}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Status messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
            {success}
          </div>
        )}

        {/* ── Overtime Rules ─────────────────────────────────────────── */}
        <Card>
          <SectionHeader
            isExpanded={expanded.overtime}
            onToggle={() => toggleSection('overtime')}
            icon={<div className="bg-orange-100 p-2 rounded-lg"><Clock className="h-5 w-5 text-orange-600" /></div>}
            title="Overtime Rules"
            description="Configure when overtime rates apply based on time of day and day of week"
          />
          {expanded.overtime && (
            <CardContent className="space-y-6 border-t border-neutral-100 pt-4">

              {/* Tab selector: Default / Weekly / Fortnightly */}
              <div className="flex gap-1 bg-neutral-100 rounded-lg p-1 w-fit">
                {(['default', 'weekly', 'fortnightly'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setOvertimeTab(tab)}
                    className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      overtimeTab === tab
                        ? 'bg-white text-neutral-900 shadow-sm'
                        : 'text-neutral-500 hover:text-neutral-700'
                    }`}
                  >
                    {tab === 'default' ? 'Default' : tab === 'weekly' ? 'Weekly staff' : 'Fortnightly staff'}
                    {tab !== 'default' && (
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        (tab === 'weekly' ? weeklyOTPolicy : fortnightlyOTPolicy)
                          ? 'bg-orange-400'
                          : 'bg-neutral-300'
                      }`} />
                    )}
                  </button>
                ))}
              </div>

              {overtimeTab === 'default' ? (
                <OvertimeRulesForm
                  rules={defaultOTRules}
                  onChange={(field, value) =>
                    updateField(field as keyof OrgPolicies, value as OrgPolicies[keyof OrgPolicies])
                  }
                />
              ) : (
                <FrequencyOvertimePanel
                  orgId={orgId!}
                  frequency={overtimeTab}
                  defaultRules={defaultOTRules}
                  policy={overtimeTab === 'weekly' ? weeklyOTPolicy : fortnightlyOTPolicy}
                  onPolicyChange={p =>
                    overtimeTab === 'weekly' ? setWeeklyOTPolicy(p) : setFortnightlyOTPolicy(p)
                  }
                />
              )}
            </CardContent>
          )}
        </Card>

        {/* ── Period Thresholds ───────────────────────────────────────── */}
        <Card>
          <SectionHeader
            isExpanded={expanded.thresholds}
            onToggle={() => toggleSection('thresholds')}
            icon={<div className="bg-blue-100 p-2 rounded-lg"><Sun className="h-5 w-5 text-blue-600" /></div>}
            title="Period Thresholds"
            description="Hours before overtime or time in lieu kicks in for the pay period"
          />
          {expanded.thresholds && (
            <CardContent className="space-y-4 border-t border-neutral-100 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <NumberInput
                  label="Weekly overtime threshold"
                  value={policies.weekly_overtime_threshold_hours}
                  onChange={v => updateField('weekly_overtime_threshold_hours', v)}
                  min={1} max={200} step={0.5}
                  suffix="hours"
                  helpText="Hours beyond this in a weekly pay period become overtime"
                />
                <NumberInput
                  label="Fortnightly threshold with no roster"
                  value={policies.fortnightly_overtime_threshold_hours}
                  onChange={v => updateField('fortnightly_overtime_threshold_hours', v)}
                  min={1} max={400} step={0.5}
                  suffix="hours"
                  helpText="Only for fortnightly employees who have no roster. Everyone else's threshold is their rostered hours for the fortnight, and paid leave counts towards it."
                />
              </div>
              <SelectInput
                label="Fortnightly staff overtime"
                value={policies.fortnightly_excess_category}
                onChange={v => updateField('fortnightly_excess_category', v as OrgPolicies['fortnightly_excess_category'])}
                options={[
                  { value: 'Time in Lieu', label: 'Banked as time in lieu unless they opt out' },
                  { value: 'Overtime (1.5x)', label: 'Paid unless they agree to time in lieu' },
                ]}
                helpText="Covers every overtime hour of a fortnightly employee: past the threshold, outside hours, weekends and public holidays worked. An employee opts out on their own timesheet with “Go back to paid overtime”."
              />
              <p className="text-sm text-neutral-600">
                Otherwise overtime is paid: weekly staff always, and fortnightly staff when set to paid above. An
                employee who would rather bank it as time in lieu asks from their own
                timesheet and accepts a written agreement, which someone with an HR seat approves on their staff
                profile. Only then is their overtime banked, hour for hour.
              </p>
            </CardContent>
          )}
        </Card>

        {/* ── Lunch Deduction ─────────────────────────────────────────── */}
        <Card>
          <SectionHeader
            isExpanded={expanded.lunch}
            onToggle={() => toggleSection('lunch')}
            icon={<div className="bg-green-100 p-2 rounded-lg"><Coffee className="h-5 w-5 text-green-600" /></div>}
            title="Lunch Deduction"
            description="Automatic lunch break deduction from clock sessions"
          />
          {expanded.lunch && (
            <CardContent className="border-t border-neutral-100 pt-4">
              <NumberInput
                label="Deduct lunch when session exceeds"
                value={policies.lunch_deduction_threshold_hours}
                onChange={v => updateField('lunch_deduction_threshold_hours', v)}
                min={0} max={12} step={0.5}
                suffix="hours"
                helpText="Lunch is automatically deducted from sessions longer than this. Where the work schedule says when lunch starts, only the part of the break the session covers comes off. Set to 0 to always deduct."
              />
            </CardContent>
          )}
        </Card>

        {/* ── Public Holidays ─────────────────────────────────────────── */}
        <Card>
          <SectionHeader
            isExpanded={expanded.publicHoliday}
            onToggle={() => toggleSection('publicHoliday')}
            icon={<div className="bg-amber-100 p-2 rounded-lg"><Calendar className="h-5 w-5 text-amber-600" /></div>}
            title="Public Holiday Pay"
            description="Rules for public holiday pay entitlements"
          />
          {expanded.publicHoliday && (
            <CardContent className="space-y-4 border-t border-neutral-100 pt-4">
              <Toggle
                label="Require adjacent working day"
                checked={policies.ph_require_adjacent_workday}
                onChange={v => updateField('ph_require_adjacent_workday', v)}
                helpText="Employee must work the day before or after a public holiday to qualify for PH pay"
              />
              {policies.ph_require_adjacent_workday && (
                <SelectInput
                  label="If employee didn't work adjacent day"
                  value={policies.ph_noshow_action}
                  onChange={v => updateField('ph_noshow_action', v as OrgPolicies['ph_noshow_action'])}
                  options={[
                    { value: 'pay_anyway', label: 'Pay anyway (no restriction)' },
                    { value: 'flag_review', label: 'Flag for manager review' },
                    { value: 'skip_entry', label: 'Do not create PH pay entry' },
                  ]}
                />
              )}
            </CardContent>
          )}
        </Card>

        {/* ── Sick Leave ──────────────────────────────────────────────── */}
        <Card>
          <SectionHeader
            isExpanded={expanded.sickLeave}
            onToggle={() => toggleSection('sickLeave')}
            icon={<div className="bg-red-100 p-2 rounded-lg"><Stethoscope className="h-5 w-5 text-red-600" /></div>}
            title="Sick Leave & Doctor's Certificates"
            description="Rules for when medical certificates are required"
          />
          {expanded.sickLeave && (() => {
            const anyRuleActive =
              policies.sick_cert_always_required ||
              policies.sick_cert_required_after_days > 0 ||
              policies.sick_cert_adjacent_to_weekend ||
              policies.sick_cert_adjacent_to_public_holiday ||
              policies.sick_cert_adjacent_to_other_leave;
            return (
              <CardContent className="space-y-5 border-t border-neutral-100 pt-4">
                <p className="text-xs text-neutral-500">
                  Configure when employees must provide a doctor's certificate for personal/sick leave.
                  Multiple rules can be active at the same time — whichever fires first triggers the
                  enforcement action below.
                </p>

                <Toggle
                  label="Always require a doctor's certificate"
                  checked={policies.sick_cert_always_required}
                  onChange={v => updateField('sick_cert_always_required', v)}
                  helpText="Master switch — every personal-leave request needs a certificate. Overrides the other rules."
                />

                {!policies.sick_cert_always_required && (
                  <>
                    <div>
                      <NumberInput
                        label="Require if leave is longer than"
                        value={policies.sick_cert_required_after_days}
                        onChange={v => updateField('sick_cert_required_after_days', Math.round(v))}
                        min={0} max={30} step={1}
                        suffix="consecutive days"
                        helpText="0 = this rule is off"
                      />
                      {policies.sick_cert_required_after_days > 0 && (
                        <div className="mt-2 ml-1">
                          <Toggle
                            label="Detect split requests (treat back-to-back personal-leave requests as one span)"
                            checked={policies.sick_cert_consecutive_split_detection}
                            onChange={v => updateField('sick_cert_consecutive_split_detection', v)}
                            helpText="Stops employees bypassing the threshold by submitting consecutive 1-day requests."
                          />
                        </div>
                      )}
                    </div>

                    <Toggle
                      label="Require if leave is adjacent to a weekend"
                      checked={policies.sick_cert_adjacent_to_weekend}
                      onChange={v => updateField('sick_cert_adjacent_to_weekend', v)}
                      helpText="Fires when the leave starts on a Monday or ends on a Friday."
                    />

                    <Toggle
                      label="Require if leave is adjacent to a public holiday"
                      checked={policies.sick_cert_adjacent_to_public_holiday}
                      onChange={v => updateField('sick_cert_adjacent_to_public_holiday', v)}
                      helpText="Fires when the working day immediately before or after the leave is a public holiday."
                    />

                    <Toggle
                      label="Require if leave is adjacent to other leave (e.g. annual)"
                      checked={policies.sick_cert_adjacent_to_other_leave}
                      onChange={v => updateField('sick_cert_adjacent_to_other_leave', v)}
                      helpText="Fires when another approved or pending leave request (any type other than personal) abuts this one."
                    />
                  </>
                )}

                {anyRuleActive && (
                  <div className="pt-2 border-t border-neutral-100">
                    <SelectInput
                      label="Enforcement action (applies to any triggered rule above)"
                      value={policies.sick_cert_enforcement}
                      onChange={v => updateField('sick_cert_enforcement', v as OrgPolicies['sick_cert_enforcement'])}
                      options={[
                        { value: 'info_only', label: 'Information only (show on leave request)' },
                        { value: 'warn_approver', label: 'Warn the approver during review' },
                        { value: 'block_approval', label: 'Block approval until certificate uploaded' },
                      ]}
                    />
                  </div>
                )}
              </CardContent>
            );
          })()}
        </Card>

        {/* ── Leave Accrual ───────────────────────────────────────────── */}
        <Card>
          <SectionHeader
            isExpanded={expanded.leaveAccrual}
            onToggle={() => toggleSection('leaveAccrual')}
            icon={<div className="bg-indigo-100 p-2 rounded-lg"><Palmtree className="h-5 w-5 text-indigo-600" /></div>}
            title="Leave Accrual & Caps"
            description="Annual and personal leave accrual rates and maximum balances"
          />
          {expanded.leaveAccrual && (
            <CardContent className="space-y-6 border-t border-neutral-100 pt-4">
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Annual Leave</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumberInput
                    label="Accrual rate"
                    value={policies.annual_leave_accrual_hours}
                    onChange={v => updateField('annual_leave_accrual_hours', v)}
                    min={0} max={1000} step={0.5}
                    suffix="hours/year"
                    helpText="Standard: 152h (4 weeks x 38h)"
                  />
                  <NumberInput
                    label="Maximum balance"
                    value={policies.annual_leave_max_balance_hours}
                    onChange={v => updateField('annual_leave_max_balance_hours', v)}
                    min={0} max={5000} step={0.5}
                    suffix="hours"
                    helpText="0 = unlimited"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Personal / Sick Leave</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumberInput
                    label="Accrual rate"
                    value={policies.personal_leave_accrual_hours}
                    onChange={v => updateField('personal_leave_accrual_hours', v)}
                    min={0} max={1000} step={0.5}
                    suffix="hours/year"
                    helpText="Standard: 76h (10 days x 7.6h)"
                  />
                  <NumberInput
                    label="Maximum balance"
                    value={policies.personal_leave_max_balance_hours}
                    onChange={v => updateField('personal_leave_max_balance_hours', v)}
                    min={0} max={5000} step={0.5}
                    suffix="hours"
                    helpText="0 = unlimited"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Long Service Leave</h4>
                <p className="text-xs text-neutral-500">
                  Accrual only applies to employees who have a Long Service Leave balance set up on their Employee Profile, under Leave entitlements.
                  Set to 0 to disable accrual.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumberInput
                    label="Accrual rate"
                    value={policies.long_service_leave_accrual_hours}
                    onChange={v => updateField('long_service_leave_accrual_hours', v)}
                    min={0} max={1000} step={0.1}
                    suffix="hours/year"
                    helpText="0 = disabled"
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* ── Save Button ─────────────────────────────────────────────── */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="px-8"
          >
            {saving ? 'Saving...' : 'Save Policies'}
          </Button>
        </div>

        {/* ── Policy documents ────────────────────────────────────────── */}
        {/* Saved on their own, not by Save Policies above. */}
        <Card>
          <SectionHeader
            isExpanded={expanded.documents}
            onToggle={() => toggleSection('documents')}
            icon={<div className="bg-lime-100 p-2 rounded-lg"><FileText className="h-5 w-5 text-lime-700" /></div>}
            title="Policy documents"
            description="The written hours, breaks and leave policies staff read and acknowledge"
          />
          {expanded.documents && (
            <CardContent className="border-t border-neutral-100 pt-4">
              <EmploymentPolicyDocuments />
            </CardContent>
          )}
        </Card>

      </div>
    </div>
  );
}
